// THEMIS <-> TKNE step link ("S" / Step orb path: a task big enough to need its
// own ORDER is run as a protocol).
//
// Stored shape (no new columns):
//   step -> protocol : DesignCheckItem.protocolId inside designs.basic_stages JSON
//   protocol -> step : a hidden tag line appended to protocols.notes (the only
//                      free-form field on `protocols`):
//                      ⟦tkne:{"designId":"...","stageKey":"...","project":"...","step":"..."}⟧
//                      THEMIS strips it before showing/editing notes and re-attaches on save.
import { supabase } from '../../lib/supabase';
import { writeNav } from '../../lib/navPersist';

export interface TkneLink {
  designId: string;
  /** Id of the step (basic_stages item) in the design. */
  stageKey: string;
  project?: string;
  step?: string;
}

const TAG_RE = /\n?⟦tkne:(\{.*?\})⟧/;

export function parseTkneLink(notes: string | null | undefined): TkneLink | null {
  const m = notes ? TAG_RE.exec(notes) : null;
  if (!m) return null;
  try {
    const v = JSON.parse(m[1]);
    return v && typeof v.designId === 'string' && typeof v.stageKey === 'string' ? (v as TkneLink) : null;
  } catch {
    return null;
  }
}

export function stripTkneLink(notes: string | null | undefined): string {
  return (notes ?? '').replace(TAG_RE, '');
}

export function withTkneLink(notes: string | null | undefined, link: TkneLink | null): string {
  const base = stripTkneLink(notes);
  return link ? `${base}\n⟦tkne:${JSON.stringify(link)}⟧` : base;
}

/** Pending deep-link into TKNE (set by THEMIS "From TKNE" tap, consumed by TkneTool on mount). */
export function openTkneAt(link: TkneLink): void {
  // TkneTool restores the open project from this nav key on mount.
  writeNav('tkne', { designId: link.designId });
  tkneFocus = { stepId: link.stageKey };
}
let tkneFocus: { stepId: string } | null = null;
export function consumeTkneFocus(): { stepId: string } | null {
  const f = tkneFocus;
  tkneFocus = null;
  return f;
}

/** Pending protocol to open when THEMIS mounts (ToolHost's onOpenTool drops opts). */
let themisPending: string | null = null;
export function openThemisProtocol(protocolId: string): void {
  themisPending = protocolId;
}
export function consumeThemisPending(): string | null {
  const v = themisPending;
  themisPending = null;
  return v;
}

export interface StepForProtocol {
  userId: string;
  designId: string;
  project: string;
  stageKey: string;
  title: string;
  subItems: string[];
  existingProtocolId?: string | null;
}

/**
 * Returns the protocol id to open. Reuses `existingProtocolId` if that
 * protocol still exists; otherwise creates a protocol (title + sub-items as
 * steps) tagged back to the step. Caller stores the id on the step.
 */
export async function ensureProtocolForStep(
  s: StepForProtocol,
): Promise<{ protocolId?: string; created?: boolean; error?: string }> {
  try {
    if (s.existingProtocolId) {
      const { data } = await supabase
        .from('protocols')
        .select('id')
        .eq('id', s.existingProtocolId)
        .eq('user_id', s.userId)
        .maybeSingle();
      if (data) return { protocolId: s.existingProtocolId, created: false };
    }
    const { count } = await supabase
      .from('protocols')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', s.userId)
      .is('group_id', null);
    const link: TkneLink = { designId: s.designId, stageKey: s.stageKey, project: s.project, step: s.title };
    const { data: proto, error } = await supabase
      .from('protocols')
      .insert({
        user_id: s.userId,
        title: s.title.trim() || 'Untitled step',
        group_id: null,
        order: count ?? 0,
        notes: withTkneLink('', link),
        created_at: new Date().toISOString(),
      })
      .select('id')
      .single();
    if (error || !proto) return { error: error?.message ?? 'Could not create protocol' };
    const protocolId = (proto as any).id as string;
    const subs = s.subItems.map((t) => t.trim()).filter(Boolean);
    const items = (subs.length ? subs : [s.title.trim() || 'Do it']).map((title, i) => ({
      user_id: s.userId,
      protocol_id: protocolId,
      title,
      done: false,
      duration_minutes: 1,
      order: i,
      created_at: new Date().toISOString(),
    }));
    const { error: itemErr } = await supabase.from('protocol_items').insert(items);
    if (itemErr) return { protocolId, created: true, error: itemErr.message };
    return { protocolId, created: true };
  } catch (e: any) {
    return { error: e?.message ?? String(e) };
  }
}
